import {
    HydraBestPracticeRoutes,
    HydraRoutingService,
} from '../services/hydra-routing-service'
import { RouteOptimizerService } from '../services/route-optimiser-service'
import { BPRIdDetails, SchedulesService } from '../services/schedules-service'
import { VesselPlansService } from '../services/vessel-plans-service'
import { RouteEditModel } from './route-edit-model'
import { ScheduleEntry, SimplifiedPortCall } from './types/voyage-schedules-model.types'
import { VesselPlanningModel, VoyagePlan } from './vessel-planning-model'
import { VoyageBestPracticeRoutesModelBase } from './voyage-best-practice-routes-model'
import {
    FeatureOrigin,
    RouteGeometry,
    VoyageRoute,
    VoyageRouteLineFeature,
    VoyageRoutePointFeature,
    VoyageRoutingModel,
} from './voyage-routing-model'
import { VoyageSchedulesModel } from './voyage-schedules-model'
import { WeatherModel } from './weather-model'
import { BprAPIResponse, BprApiRoutePointFeature } from './types/voyage-best-practice-routes-impl.types'
import { addPrefillValuesToPassagePlan, calcUKC, isStarRoute } from '../utils/route-utils'
import { UUID, deepCopy, splitRouteGeometry, isOnline } from '../utils/helpers'
import { starRouteFetchDisabled } from '../../local_modules/voyage-suggested-routes/src/helper'
import { VoyageDialogPopupManager } from '../../local_modules/voyage-dialog-popup/src/voyage-dialog-popup-manager'
import { SuggestedRouteEvents, SuggestedRouteStatusEvents } from '../../local_modules/voyage-suggested-routes/src/types'
import { AlertType, VesselAlertsModel } from './vessel-alerts-model'
import { VoyageImoModel } from './voyage-imo-model'
import { IndexedDBService } from '../../indexed-db/services/db_service'
import { ISOPENOCCEANROUTEENABLED } from './feature-flag-model-impl'
import { getDistance } from 'ol/sphere'

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;
const OPEN_OCEAN_MIN_DISTANCE_NM = 1000;
export enum BestPracticeRoutesEvents {
    RouteAvaliable = 'route-avaliable',
    RouteNotAvaliable = 'route-not-avaliable',
    RouteIsCalculating = 'route-is-calculating',
    RouteDoneFetching = 'route-done-fetching',
    FetchingRoute = 'fetching-route',
}

export enum BestPracticeRouteStatus {
    HasBestPracticeRoute = 'HasBestPracticeRoute',
    HasNotBestPracticeRoute = 'HasNotBestPracticeRoute',
    Empty = 'Empty',
}

// getTopStarRoute returns the plan's top-ranked Star Route: the one with the lowest rank, which a BPR
// job names "Star Route <rank>".
export function getTopStarRoute(routes: VoyageRoute[]): VoyageRoute | undefined {
    const rankOf = (route: VoyageRoute): number => {
        const rank = Number(route.name?.match(/^Star Route (\d+)$/)?.[1]);
        return Number.isFinite(rank) ? rank : Number.POSITIVE_INFINITY;
    };
    return routes.filter(isStarRoute).sort((a, b) => rankOf(a) - rankOf(b))[0];
}

// starRouteRankingAlertId is the id of a schedule's "Star Route Ranking Updated" banner. It is fixed per
// schedule so a newer banner replaces the previous one instead of stacking.
export const starRouteRankingAlertId = (scheduleId: string): string => `star-route-ranking-updated-${scheduleId}`;

// starRouteAlertImo is the IMO the StarRoute banners are added under: the alert panel shows the alerts of
// the selected vessel (VoyageImoModel.imo). In cloud, appenv.vesselIMONumber is not the selected vessel.
const starRouteAlertImo = (): number => VoyageImoModel?.imo ?? (<any>window)["appenv"].vesselIMONumber;

export class VoyageBestPracticeRoutesModelImpl extends VoyageBestPracticeRoutesModelBase {
    constructor() {
        super()
        // Listen for the event to create a user route
        RouteEditModel!.addEventListener('create-user-route`', (e: Event) =>
            this.ifRouteShouldBeFetched(
                false,
                (<CustomEvent>e).detail.sceduleId,
            ),
        )

        this._fetchAllBPRIDs = this._fetchAllBPRIDs.bind(this)
        this._refreshSchedule = this._refreshSchedule.bind(this)
        this._getScheduleCall = this._getScheduleCall.bind(this)

        // Listen for the schedule event
        VoyageSchedulesModel!.addEventListener('schedule-updated', this._refreshSchedule);
        this._refreshSchedule()
    }
    private _plan: VoyagePlan | null = null
    private _shouldRouteBeFetched: boolean = true
    private _jobs: Array<{ sceduleId: string; status: string }> = []
    private _rankedBPRsJobs: Map<string, { status: "pending" | "complete" | "failed" }> = new Map();
    private _deletedBPR: Map<string, string[] > = new Map();
    protected _bestPractiveExistsStatus: Map<string, BestPracticeRouteStatus> =
        new Map()
    protected _BPRIdsByScheduleID: Map<string, BPRIdDetails[]> =
        new Map()

    //BPR routes per schedule properties
    private _portOmitsMap: Map<string,SimplifiedPortCall> = new Map();
    private _schedule: Map<string,ScheduleEntry> = new Map();
    private _BPRsBySchedule: Map<string, VoyageRoute[]> = new Map();

    // OOR routes per schedule properties
    private _OORsBySchedule: Map<string, VoyageRoute[]> = new Map();
    private _deletedOOR: Map<string, string[]> = new Map();

    private _setJob(sceduleId: string, status: string) {
        this._jobs.push({ sceduleId: sceduleId, status: status })
    }

    getBPRsByScheduleId(scheduleId: string): VoyageRoute[] {
        return this._BPRsBySchedule.get(scheduleId) || [];
    }

    getDeletedByScheduleId(scheduleId: string): string[] {
        return this._deletedBPR.get(scheduleId) || [];
    }

    isOpenOceanRouteEnabled(): boolean {
        return localStorage.getItem(ISOPENOCCEANROUTEENABLED) === 'true';
    }

    getDeletedOORByScheduleId(scheduleId: string): string[] {
        return this._deletedOOR.get(scheduleId) || [];
    }

    private _isLongVoyage(plan: VoyagePlan): boolean {
        const { startPosition, endPosition } = plan;
        if (!startPosition || !endPosition) return false;
        // getDistance returns metres; 1 nm = 1852 m
        return getDistance(startPosition, endPosition) / 1852 >= OPEN_OCEAN_MIN_DISTANCE_NM;
    }

    // Star Routes' validity is unconfirmed while a recalculation is in flight, so hide them on the
    // map for its duration rather than risk showing one that's about to be replaced or invalidated.
    private _setStarRoutesVisibility(routes: VoyageRoute[], scheduleId: string, visible: boolean): void {
        for (const route of routes) {
            if (route.routeCreatedBy === 'system' && route.uiType !== 'OOR') {
                VoyageRoutingModel!._setRouteViewStatus(route.routeId, scheduleId, visible);
            }
        }
    }

    updateDeletedOOR(scheduleId: string, oorId: string, reset: boolean): void {
        if (reset) {
            this._deletedOOR.set(scheduleId, []);
        } else {
            const existing = this._deletedOOR.get(scheduleId) || [];
            existing.push(oorId);
            this._deletedOOR.set(scheduleId, existing);
        }
    }

    hasPendingBPRJob(scheduleId: string): boolean {
        const job = this._rankedBPRsJobs.get(scheduleId);
        return job !== undefined && job.status === "pending";
    }

    ifRouteShouldBeFetched(status: boolean, sceduleId: string) {
        if (sceduleId === this._plan?.scheduleId) {
            this._shouldRouteBeFetched = status
        }
    }

    getBestPracticeRouteExistence(sceduleId: string) {
        if (this._bestPractiveExistsStatus.has(sceduleId)) {
            return this._bestPractiveExistsStatus.get(sceduleId)!
        }
        return BestPracticeRouteStatus.Empty
    }

    setBestPracticeRouteExistence(
        sceduleId: string,
        status: BestPracticeRouteStatus,
    ) {
        this._bestPractiveExistsStatus.set(sceduleId, status)
    }

    calcUKCs(plan: VoyagePlan, splitRoute: [RouteGeometry, RouteGeometry, RouteGeometry]) {
        for (const [sectionIdx, routeSection] of splitRoute.entries()) {
            var draft: number|null = null;

            //calculate only for the index 1
            draft = Math.max(plan.foreDraft,plan.aftDraft)

            if (!draft) {
                continue;
            }
    
            for (const feature of routeSection) {
                if (feature.properties.type !== "Hard") continue;
                const minDepthRaw = feature.properties.userInput?.min_depth ?? feature.properties.min_depth;
                const catZocRaw = feature.properties.userInput?.catzoc ?? feature.properties.catzoc;
                let tideRaw = feature.properties.tide ?? feature.properties.userInput?.tide;

                // When tide is not set, it is by default 0.
                // Tide property is being set to 0 in 'populateWaypoints' function, which is 
                // probably not he right place to do it. The 'populateWaypoints' is editing the
                // waypoint properties, but it should only return the waypoints used for the passage plan UI
                if (!tideRaw) {
                    tideRaw = 0;
                }

                // CatZoc is only used for pilot-> pilot
                const ignoreCatZoc = sectionIdx !== 1;
                if (minDepthRaw && (!ignoreCatZoc || catZocRaw)) {
                    const ukc = calcUKC(minDepthRaw, tideRaw, draft, catZocRaw, ignoreCatZoc);
                    feature.properties.ukc = ukc;
                }
            }
        }

        return splitRoute;
    }

    // remove this function as it is not used
    async getAutoRoute(plan: VoyagePlan): Promise<VoyageRoute | null> {
        if (!this._shouldRouteBeFetched) {
            return null
        }

        const routeCalcInProgress = this._jobs.find(
            (el) =>
                el.sceduleId === plan.scheduleId && el.status === 'calculating',
        )
        if (routeCalcInProgress) {
            return null
        }
        this._setJob(plan.scheduleId, 'calculating')
        this._plan = plan

        this.dispatchEvent(
            new CustomEvent(SuggestedRouteEvents.BestPracticeRouteChanged, {
                detail: {
                    isRouteAvailable: BestPracticeRoutesEvents.FetchingRoute,
                },
            }),
        )

        const forecast = WeatherModel!.latestForecastInfo
        const options = {
            imo_number: plan.imoNo || isVesselOnCloud ? VoyageImoModel?.imo :  (<any>window)['appenv'].vesselIMONumber,
            forecast: forecast?.name || '',
            etd: new Date(plan.etd),
            eta: new Date(plan.eta),
            departure_terminal: plan.departureTerminalCode,
            arrival_terminal: plan.arrivalTerminalCode,
            draught_fore: plan.foreDraft,
            draught_aft: plan.aftDraft,
            reefer_consumption: plan.numberOfReefers,
            maximum_segment_distance: 300,
            transverse_metacentric_height: plan.gmt || 0,
        }

        try {
            let route

            const result = await HydraRoutingService.getBestPracticeRoute(
                options,
            )
            const idx = this._jobs.findIndex(
                (el) => el.sceduleId === plan.scheduleId,
            )

            if (!result) {
                this._jobs.splice(idx, 1)
                this.dispatchEvent(
                    new CustomEvent(SuggestedRouteEvents.BestPracticeRouteChanged, {
                        detail: {
                            isRouteAvailable:
                                BestPracticeRoutesEvents.RouteNotAvaliable,
                        },
                    }),
                )
                this.setBestPracticeRouteExistence(
                    this._plan.scheduleId,
                    BestPracticeRouteStatus.HasNotBestPracticeRoute,
                )
                return null
            }

            route = {
                arrival_terminal: result.arrival_terminal,
                departure_terminal: result.departure_terminal,
                eta: result.eta,
                etd: result.etd,
                route: result.routes[0],
            }

            if (route) {
                const modifiedRoute = await this._processBestPracticeRoute(
                    route,
                    plan,
                )
                this._jobs.splice(idx, 1)
                this.setBestPracticeRouteExistence(
                    this._plan.scheduleId,
                    BestPracticeRouteStatus.HasBestPracticeRoute,
                )
                VoyageRoutingModel!.saveRoute(
                    modifiedRoute,
                    0,
                    this._plan.scheduleId,
                    ''
                )
            }

            this.dispatchEvent(
                new CustomEvent(SuggestedRouteEvents.BestPracticeRouteChanged, {
                    detail: {
                        isRouteAvailable:
                            BestPracticeRoutesEvents.RouteDoneFetching,
                    },
                }),
            )

        } catch (error) {
           console.error('error while fetching best practice route', error)
        }

        return null
    }


    private _checkForUKC(plan: VoyagePlan, route: VoyageRoute) {
        const res = splitRouteGeometry(route)
        const splitedRoute = this.calcUKCs(plan, res)

        //we need to find it only for Pilot to Pilot
        const routeHasNegativeUKC = splitedRoute[1].find((el) => {
            if (el.properties.ukc <= 0) {
                return true
            }
        })

        return routeHasNegativeUKC ? null : route
    }

    private async _processBestPracticeRoute(
        res: HydraBestPracticeRoutes,
        plan: VoyagePlan,
    ) {
        const hardWaypoints = res.route.route_geometry.filter((el) => {
            return el.properties.type === 'Hard'
        })

        const routeGeometry =
            this._convertFromHydraBestPracticeRoutes_old(hardWaypoints)

        const route: VoyageRoute = {
            routeId: '',
            uiType: `UserRoute0`,
            name: 'AutoRoute',
            route_geometry: routeGeometry,
            bestPracticeRouteId: res.route.best_practice_route_id,
        }

        return route
    }

    private _convertFromHydraBestPracticeRoutes_old(
        points: VoyageRoutePointFeature[],
    ): RouteGeometry {
        const result: RouteGeometry = []

        for (let i = 0; i < points.length; i++) {
            const wp: VoyageRoutePointFeature = {
                type: 'Feature',
                geometry: {
                    coordinates: points[i].geometry.coordinates,
                    type: 'Point',
                },
                properties: points[i].properties,
            }
            result.push(wp)

            if (i !== points.length - 1) {
                let distance = wp.properties.distance / 1000 // convert from meters to km
                let speed_over_ground = wp.properties.speed_over_ground
                let speed_through_water = wp.properties.speed_through_water

                // Add leg and move the relevant properties to the leg
                result.push({
                    type: 'Feature',
                    geometry: {
                        coordinates: [
                            wp.geometry.coordinates,
                            points[i + 1].geometry.coordinates,
                        ],
                        type: 'LineString',
                    },
                    properties: {
                        leg_type:
                            wp.properties.leg_type === 'rhumb_line'
                                ? 'Rhumbline'
                                : 'GreatCircle',
                        distance: distance,
                        course: wp.properties.course_over_ground,
                        speed_over_ground: speed_over_ground,
                        speed_through_water: speed_through_water,
                        propulsion_power: wp.properties.propulsion_power,
                        propulsion_power_lower:
                            wp.properties.propulsion_power_lower,
                        propulsion_power_upper:
                            wp.properties.propulsion_power_upper,
                        main_engine_rpm: wp.properties.main_engine_rpm,
                        main_engine_rpm_lower:
                            wp.properties.main_engine_rpm_lower,
                        main_engine_rpm_upper:
                            wp.properties.main_engine_rpm_upper,
                    },
                })

                delete wp.properties.leg_type
                delete wp.properties.distance
                delete wp.properties.course_over_ground
                delete wp.properties.speed_over_ground
                delete wp.properties.speed_through_water
                delete wp.properties.propulsion_power
                delete wp.properties.main_engine_rpm
            }
        }

        return result
    }

    //NEW BPR schedules per routes
    private async _getScheduleCall() {
        const rawSchedule = await VoyageSchedulesModel!.getScheduleFor(VoyageImoModel!.imo!) || [];
        
        // Cut off schedule after 12 weeks from now
        const cutoffDate = Date.now() + 12 * 7 * 24 * 60 * 60 * 1000; // 12 weeks in milliseconds
        const getArrivalTimestamp = (entry: ScheduleEntry) => {
            const arrivalAt = entry.arrivalPortCall?.GSIS?.arrivalAt || entry.arrivalAt;
            const timestamp = new Date(arrivalAt).getTime();
            return Number.isFinite(timestamp) ? timestamp : 0;
        };
        const filteredSchedule = rawSchedule.filter(entry => getArrivalTimestamp(entry) <= cutoffDate);
    
        // Initialize the schedule map
        this._schedule = new Map<string, ScheduleEntry>();
    
        // Process each entry in the filtered schedule
        filteredSchedule.forEach((entry, i) => {
            // Check if the entry is omitted
            if (entry.omitted) {
                // Handle omitted entry, for example, by setting a flag or adding a placeholder in the map
                if (i > 0) {
                    const prevEntry = filteredSchedule[i - 1];
                    this._portOmitsMap.set(prevEntry.id, entry.arrivalPortCall!.current);
                }
                // Skip adding this entry to the schedule map
                return;
            }
    
            // Add non-omitted entries to the schedule map
            this._schedule.set(entry.id, entry);
        });
    }

    getBPRAvaliabilityPerSchedule(sceduleId: string, departurePortCall: string, arrivalPortCall: string) {
        const bprItem = this._BPRIdsByScheduleID.get(sceduleId); 

        if (!bprItem) {
          return null;
        }
      
        const bprRoute = bprItem.find(el => {
            return el.departure_terminal_code === departurePortCall && el.arrival_terminal_code === arrivalPortCall
            }
        );

        return bprRoute || null;
    }
   

    private async _refreshSchedule() {
       await this._getScheduleCall()
       await this._fetchAllBPRIDs()
    }

    async _fetchBPRAvailability(scheduleId: string, entry: any) {
        const res = await SchedulesService.getBPRAvailability(entry.departureTerminalCode, entry.arrivalTerminalCode, entry.id);
        return { id: scheduleId, data: res };
    }

    async _fetchAllBPRIDs() {
        // Clear the existing BPR IDs
        this._BPRIdsByScheduleID.clear();

        try {

            if(!isVesselOnCloud) {
                const fetchPromises = Array.from(this._schedule.entries()).map(([scheduleId, entry]) => this._fetchBPRAvailability(scheduleId, entry));
            
                // Wait for all fetch operations to complete
                const results = await Promise.all(fetchPromises);
            
                // Process each result
                results.forEach((result) => {
                // Check if result data is not null
                if (result.data !== null) {
                    this._BPRIdsByScheduleID.set(result.id, result.data);
                }});
            } else {
                // Get the BPR IDs for each schedule
                for (const [scheduleId, entry] of this._schedule.entries()) {
                    if (entry.isBprAvailable) {
                        // Real BPR IDs from the schedule entry (not a UI placeholder UUID).
                        const bprIdList = entry.BprRouteIds ?? [];
                        this._BPRIdsByScheduleID.set(scheduleId, [{
                            schedule_id: scheduleId,
                            departure_terminal_code: entry.departureTerminalCode, 
                            arrival_terminal_code: entry.arrivalTerminalCode, 
                            metadata: {bprIdList}}]);
                    }
                }
            }

            
        
            this.dispatchEvent(new Event('bpr-lists-updated'));
        } catch (error) {
            console.error("Error fetching BPR lists:", error);
        }
    }

    async getResultForRankedBPRJobCloud(jobId: any) {
        let status = "";
        let result = null;
        while (status !== "failed" && status !== "complete" && status !== "failedPendingRetry")
            {
                result = await RouteOptimizerService.getResultForRankedBPRJob(jobId!, true);
                status = result?.status ? result.status : "Failed";
                if (result) {
                    this.dispatchEvent(
                        new CustomEvent(SuggestedRouteStatusEvents.JobProgressChanged, {
                            detail: result,
                        }),
                    );
                    if (result.stageEstimate) {
                        this.dispatchEvent(
                            new CustomEvent(SuggestedRouteStatusEvents.StageEstimateChanged, {
                                detail: result.stageEstimate,
                            }),
                        );
                    }
                }
                if (status !== "failed" && status !== "complete" && status !== "failedPendingRetry")
                {
                    this.dispatchEvent(
                        new CustomEvent(SuggestedRouteStatusEvents.StatusChanged, {
                            detail: result?.statusDetails,
                        }),
                    )
                    if (result?.stageEstimate) {
                        this.dispatchEvent(
                            new CustomEvent(SuggestedRouteStatusEvents.StageEstimateChanged, {
                                detail: result.stageEstimate,
                            }),
                        )
                    }
                    await new Promise(resolve => setTimeout(resolve, 1000)); // Wait one seconds between polls
                }
            }

        return result;
    }

    // Posts a BPR ranking job for the given BPR IDs and waits for the result, polling on cloud
    // (getResultForRankedBPRJobCloud) or edge (getResultForRankedBPRJob). Sets the schedule job to
    // "pending" and emits RankedBPRStarted so the UI reflects that a recalculation is in progress.
    private async _runBPRRankingJob(
        plan: VoyagePlan,
        latestForecastVersion: string,
        bprIds: string[],
        weatherLimits: Parameters<typeof RouteOptimizerService.postBPRRankingJob>[3],
    ): ReturnType<typeof RouteOptimizerService.getResultForRankedBPRJob> {
        const jobId = await RouteOptimizerService.postBPRRankingJob(plan, latestForecastVersion, bprIds, weatherLimits);
        // Set the job to "fetching" status
        this._rankedBPRsJobs.set(plan.scheduleId, { status: "pending" });
        this.dispatchEvent(new CustomEvent(SuggestedRouteEvents.RankedBPRStarted, {detail: {scheduleId: plan.scheduleId}}));

        // Create a job to fetch status of ranked BPRs
        // Fetch results for the ranking job
        if (isVesselOnCloud) {
            return await this.getResultForRankedBPRJobCloud(jobId!);
        }
        return await RouteOptimizerService.getResultForRankedBPRJob(jobId!, false, (pollResult) => {
            this.dispatchEvent(
                new CustomEvent(SuggestedRouteStatusEvents.JobProgressChanged, {
                    detail: pollResult,
                }),
            );
            if (pollResult?.stageEstimate) {
                this.dispatchEvent(
                    new CustomEvent(SuggestedRouteStatusEvents.StageEstimateChanged, {
                        detail: pollResult.stageEstimate,
                    }),
                )
            }
        });
    }

    /**
     * Refreshes the plan's Star Routes when the user accepts the voyage details ("Next").
     *
     * The port-pair endpoint only changes when a BPR job runs, and every new forecast precomputes the
     * voyage's Star Routes with the plan's inputs. So a BPR job is only needed when the plan inputs
     * changed, or when the top sailable precomputed Star Route (passing the UKC, heavy weather and
     * parametric roll checks) is no longer the plan's top Star Route - the one a BPR job would rank
     * first. Otherwise the plan's Star Routes are kept as they are, without fetching the port-pair endpoint.
     */
    async refreshStarRoutesForAcceptedVoyageDetails(plan: VoyagePlan, inputsChanged: boolean): Promise<void> {
        if (inputsChanged) {
            return this.getRankedBPRsForSchedule(plan, true);
        }
        if (await this._hasTopStarRouteChanged(plan)) {
            // Tell the user why the Star Routes are recalculated although the voyage details did not change.
            VesselAlertsModel!.addAlert(starRouteAlertImo(), {
                title: "Star Route Ranking Updated",
                message: "A more fuel-efficient StarRoute is available for this voyage. Recalculating the StarRoutes.",
                id: starRouteRankingAlertId(plan.scheduleId),
                type: AlertType.BestPracticeRouteUpdated,
                scheduleId: plan.scheduleId,
            });
            return this.getRankedBPRsForSchedule(plan, true);
        }
        await this._keepPlanStarRoutes(plan);
    }

    // _hasTopStarRouteChanged reports whether the top sailable precomputed Star Route differs from the
    // plan's top Star Route. Without a sailable precomputed Star Route there is nothing to compare against.
    private async _hasTopStarRouteChanged(plan: VoyagePlan): Promise<boolean> {
        if (starRouteFetchDisabled(plan.etd) || this.hasPendingBPRJob(plan.scheduleId)) {
            return false;
        }
        if ((!isOnline() && isVesselOnCloud) || plan.foreDraft == null || plan.aftDraft == null) {
            return false;
        }
        const [topSailableBPRId] = await VesselPlansService.getSailableBPRIds(plan.scheduleId, plan.foreDraft, plan.aftDraft, 1);
        if (!topSailableBPRId) {
            return false;
        }
        return topSailableBPRId !== getTopStarRoute(plan.routes)?.bestPracticeRouteId;
    }

    // _keepPlanStarRoutes keeps the plan's Star Routes as they are. The Star Routes offered by "Request
    // another star route" are restored from the last BPR job's result cached in IndexedDB when not loaded.
    private async _keepPlanStarRoutes(plan: VoyagePlan): Promise<void> {
        if (!this._BPRsBySchedule.get(plan.scheduleId)?.length) {
            try {
                const cachedBPRs = await IndexedDBService.bprs.get(plan.scheduleId);
                if (cachedBPRs?.data?.length) {
                    this._addBPRtoSchedule(cachedBPRs.data, WeatherModel!.latestForecastVersion, plan);
                }
            } catch (err) {
                console.warn('Could not restore the cached Star Routes for schedule', plan.scheduleId, err);
            }
        }
        const starRoutesInPlan = plan.routes.filter(isStarRoute).length;
        this.dispatchEvent(new CustomEvent(SuggestedRouteEvents.RankedBPRUpdated, {
            detail: { scheduleId: plan.scheduleId, bprOptimizedLength: starRoutesInPlan, ukcFailedRoutesLength: 0 },
        }));
    }

    async getRankedBPRsForSchedule(plan: VoyagePlan, triggerBprJob: boolean = false): Promise<void> {
        // check if plans etd is before the current time
        // returning if the voyage has been started for the current plan
        if(starRouteFetchDisabled(plan.etd)) {
            return;
        }
        // Check if we already have an active job for this schedule
        const existingJob = this._rankedBPRsJobs.get(plan.scheduleId);
        if (existingJob && existingJob.status === "pending") {
            return;
        }

        const BPRIDs = this._BPRIdsByScheduleID.get(plan.scheduleId)?.[0]?.metadata?.bprIdList ?? [];

        const planForPayload = plan;
        const latestForecastVersionForPayload = WeatherModel!.latestForecastVersion;
        const weatherLimits =  VoyageRoutingModel!.getWeatherLimits()
        this._BPRsBySchedule.set(plan.scheduleId, []);
        let result;
        // Track the draft-sailable BPR IDs (if any) so we can detect the case where the backend
        // considered routes sailable for the entered draft but the recalculation returns none.
        let sailableBPRIds: string[] = [];
        const vesselIsOffline = !isOnline() && isVesselOnCloud;
        // trigger BPR job only if the user has updated the plan manually
        if (triggerBprJob && !vesselIsOffline) {
            this._setStarRoutesVisibility(plan.routes, plan.scheduleId, false);
            // Always use the sailable BPR endpoint to source the BPR IDs for the calculation, so the
            // ranking job only evaluates the pre-optimised routes that are sailable for the entered
            // draft. If the calculation returns no BPRs, we retry against the full BPR list below.
            let rankingBPRIDs = BPRIDs;
            if (plan.foreDraft != null && plan.aftDraft != null) {
                sailableBPRIds = await VesselPlansService.getSailableBPRIds(plan.scheduleId, plan.foreDraft, plan.aftDraft);
                rankingBPRIDs = sailableBPRIds.length > 0 ? sailableBPRIds : BPRIDs;
            }
            result = await this._runBPRRankingJob(planForPayload, latestForecastVersionForPayload, rankingBPRIDs, weatherLimits);

            if (result && result.status === "failedPendingRetry") {
                // Extract error message from API response
                const errorMessage = result?.errorDetails?.hydraErrorBody?.message || 
                                   result?.statusDetails || 
                                   "An unknown error occurred while calculating the Star Route.";
                
                this.dispatchEvent(new CustomEvent(SuggestedRouteEvents.RetryBprJob, {
                    detail: {
                        plan: plan,
                        errorMessage: errorMessage
                    }
                }));
            }
        }

        // Open Ocean calculation failure: surface the full job log so it can be downloaded, but
        // keep processing the ranked BPRs below. A failed OOR sub-calculation can leave the
        // overall job reporting "failed" even though the Star Route (BPR) portion completed and
        // is available via the port-pair endpoint, so we must not bail out before fetching it.
        const oorError = result?.errorDetails?.oorError;
        if (this.isOpenOceanRouteEnabled() && oorError) {
            this.dispatchEvent(new CustomEvent(SuggestedRouteEvents.OorCalculationFailed, {
                detail: { scheduleId: plan.scheduleId, job: result }
            }));
        }

        let optimizedBPRLength = 0;
        let ukcFailedRoutesLength = 0;

        // Parse BPR format to VoyageRoute format
        let optimizedBPRs : BprAPIResponse[] | null = [];
        if ((result && (result.status === "complete" || oorError)) || !triggerBprJob || vesselIsOffline) {
            optimizedBPRs = await VesselPlansService.getOptimizedBPRsByPortPair(plan.departureTerminalCode, plan.arrivalTerminalCode, plan.scheduleId);

            // Fallback: the ranking job (run against the sailable BPR IDs) returned no BPRs. Retry
            // once against the full BPR list for the schedule as with the existing implementation:
            // passing an empty id array makes postBPRRankingJob omit best_practice_route_ids, so the
            // optimiser re-fetches and evaluates all routes instead of the sailable subset. A
            // successful retry flows into parsing below. Only worth doing when the first job was
            // actually restricted to a non-empty sailable subset - if sailableBPRIds was already
            // empty, that first job already evaluated the full list, so retrying would just repeat
            // the same request.
            if (!optimizedBPRs && triggerBprJob && !vesselIsOffline && sailableBPRIds.length > 0) {
                console.info(
                    "Sailable BPRs failed recalculation: the ranking job produced no BPRs. Retrying the ranking job against the full BPR list for the schedule.",
                    {
                        scheduleId: plan.scheduleId,
                        foreDraft: plan.foreDraft,
                        aftDraft: plan.aftDraft,
                        sailableBPRIdCount: sailableBPRIds.length,
                        sailableBPRIds,
                    },
                );
                // Empty id array => optimiser evaluates the full BPR list (see postBPRRankingJob).
                result = await this._runBPRRankingJob(planForPayload, latestForecastVersionForPayload, [], weatherLimits);
                optimizedBPRs = await VesselPlansService.getOptimizedBPRsByPortPair(plan.departureTerminalCode, plan.arrivalTerminalCode, plan.scheduleId);
            }

            if (!optimizedBPRs) {
                // Monitoring: even after retrying against the full BPR list, the recalculation
                // returned no BPRs. When sailable IDs were available this is the unexpected
                // depth/UKC failure worth investigating.
                if (sailableBPRIds.length > 0) {
                    console.info(
                        "Sailable BPRs failed recalculation: still no BPRs after retrying against the full BPR list (possible unexpected depth/UKC failure).",
                        {
                            scheduleId: plan.scheduleId,
                            foreDraft: plan.foreDraft,
                            aftDraft: plan.aftDraft,
                            sailableBPRIdCount: sailableBPRIds.length,
                            sailableBPRIds,
                        },
                    );
                }
                this.dispatchEvent(new CustomEvent(SuggestedRouteEvents.RankedBPRUpdated, {detail: {scheduleId: plan.scheduleId, bprOptimizedLength: 0, ukcFailedRoutesLength: 0, errorDetails: result?.errorDetails}}));
                this._rankedBPRsJobs.set(plan.scheduleId, { status: "complete" });
                VesselAlertsModel!.removeAlert(starRouteAlertImo(), starRouteRankingAlertId(plan.scheduleId));

                if (this.isOpenOceanRouteEnabled() && this._isLongVoyage(plan)) {
                    this._setStarRoutesVisibility(plan.routes, plan.scheduleId, true);
                    await this._loadDefaultOOR(plan);
                    return;
                }

                // Clear any stale suggested Star Route so setPlan triggers a map refresh, but keep the
                // user's selected route ("My StarRoute") - it stays until the user picks something else.
                const latestPlan = await VesselPlanningModel!.getCurrentPlan(plan.scheduleId);
                if (latestPlan) {
                    const { currentSelectedRouteId } = latestPlan;
                    const isStaleStarRoute = (route: VoyageRoute) => route.routeCreatedBy === 'system' && route.uiType !== 'OOR' && route.routeId !== currentSelectedRouteId;
                    if (latestPlan.routes.some(isStaleStarRoute)) {
                        latestPlan.routes = latestPlan.routes.filter(route => !isStaleStarRoute(route));
                        await VesselPlanningModel!.setPlan(plan.scheduleId, latestPlan, false);
                    }
                    // Reveal whatever Star Route(s) actually survived the recalculation (e.g. "My StarRoute").
                    this._setStarRoutesVisibility(latestPlan.routes, plan.scheduleId, true);
                }

                VoyageDialogPopupManager.createDialog({
                    headerText: `No Star Routes available`,
                    bodyText: `We regret to inform you that none of the Star Routes in our library \npass the recent UKC check based on the entered drafts. We apologize \nfor any inconvenience this may cause.`,
                });
                return;
            }

            optimizedBPRLength = optimizedBPRs.length;
            // This function can take long time to execute, so we need to make sure we're using latest plan
            const latestPlan = await VesselPlanningModel!.getCurrentPlan(plan.scheduleId);
            const bprIdsFailingUKC : string[] = [];

            for (const hydraRoute of optimizedBPRs) {
                const bprRoute = hydraRoute.routes;
                const routeGeometry = bprRoute.route_geometry;
            

                const convertedRouteGeometry = this._convertRouteGeometry(routeGeometry);

                const rank = hydraRoute?.fuel_rank || hydraRoute?.rank;
                const route: VoyageRoute = {
                    routeId: UUID(),
                    uiType: `BPR`,
                    routeCreatedBy: 'system',
                    name: bprRoute.uiType === 'OOR' ? `Open Ocean ${rank}` : `Star Route ${rank}`,
                    route_geometry: convertedRouteGeometry,
                    bestPracticeRouteId: bprRoute.best_practice_route_id,
                    creationTime: new Date().toISOString(),
                    passagePlan: {
                        description: "",
                        reminders: "",
                        notEcdisCompliant: false,
                        encCellsUsed: "",
                        booksAndTables: "",
                        chartsAndLightsNotes: "",
                        sailingDirections: "",
                        listOfRadioSignals: "",
                        listOfLights: "",
                        gpsEcdisRadarMap: "",
                        paperCharts: "",
                        pubCorrectedToWeek: "",
                        tideTables: "",
                        publicationsNotes: "",
                        mastersOrders: "",
                        captain: "",
                        navigationalOfficer: "",
                        planDate: null,
                        safetyMessages: {
                            navareaRegions: "",
                            navtexStations: ""
                        },
                        weatherInfo: {
                            navareaRegions: "",
                            navtexStations: "",
                            info: ""
                        },
                        equipmentStatus: {
                            operationalCondition: "",
                            failures: ""
                        },
                        weatherRouting: {
                            required: false,            
                            acknowledged: false,     
                            pilotPointAcknowledged: false 
                        }
                    }
                }
                // const startpoint = bprRoute.route_geometry[0];
                const endpoint = bprRoute.route_geometry[bprRoute.route_geometry.length - 1];
                const resultDuration = new Date(endpoint.properties.timestamp!).getTime() - new Date(bprRoute.etd).getTime();
                route.route_info = {
                    eta: new Date(bprRoute.route_info?.eta)?.toISOString(),
                    etd: new Date(latestPlan!.etd).toISOString(),
                    speed_over_ground: bprRoute.route_info?.speed_over_ground,
                    co2: -1,
                    totals: {
                        eca: { time: 0, fuel_cost: 0, vessel_cost: 0, distance: 0, fuel: 0 },
                        non_eca: {
                            time: resultDuration/60*60*1000, // convert to hours
                            fuel_cost: 0, 
                            vessel_cost: 0, // Not available
                            distance: bprRoute.route_info?.totals?.non_eca?.distance,
                            fuel:bprRoute.route_info?.totals?.non_eca?.fuel
                        }
                    },
                    rpm: -1, // RPM for entire voyage? TODO - remove this, it is pretty useless
                    weather: { // Weather will be added after this
                        forecast_versions: [ ],
                        subscriptions: [],
                        summary: bprRoute.route_info?.weather?.summary
                    },
                    calculationInput: {
                        forecast_versions: [ latestForecastVersionForPayload ],
                        draftFore: planForPayload.foreDraft,
                        draftAft: planForPayload.aftDraft,
                        gmt: planForPayload.gmt || 0,
                        etd: new Date(planForPayload.etd).toISOString(),
                        eta: new Date(planForPayload.eta).toISOString(),
                        weatherLimits: VoyageRoutingModel!.getWeatherLimits()
                    },
                    hydraErrors: bprRoute.route_info?.hydraErrors,
                };
                addPrefillValuesToPassagePlan(route!.passagePlan!);

                if (this._BPRsBySchedule.has(plan.scheduleId)) {
                    this._BPRsBySchedule.get(plan.scheduleId)!.push(route);
                } else {
                    this._BPRsBySchedule.set(plan.scheduleId, [route]);
                }
            }

             // Save route to plan if it not already exists.
             // Check again in latest plan as it could have changed during the ranking job duration
            const bprExistsInLatestPlan = latestPlan!.routes.find((route) => {
                return route.routeCreatedBy === 'system';
            });
            
            const latestBPRs = this._BPRsBySchedule.get(plan.scheduleId);
            const topRankedBPR = latestBPRs![0];
            const { currentSelectedRouteId } = latestPlan!;
            let routes = deepCopy(latestPlan!.routes);

            if (!bprExistsInLatestPlan) {
                // Index 0 is the top-ranked BPR
                routes.push(topRankedBPR);
            } else {
                // remove all BPRs unless the route is selected by the user
                routes = routes.filter(route => route.routeCreatedBy !== 'system' || route.routeId === currentSelectedRouteId);

                // Check if the selected route is a system-generated BPR
                const selectedRouteBPR = routes.find(route => route.routeId === currentSelectedRouteId && route.routeCreatedBy === 'system');

                // if current selected route is BPR, then check in the latest BPRs
                if (selectedRouteBPR) {
                    const optimizedBPR = latestBPRs!.find(route => route.bestPracticeRouteId === selectedRouteBPR.bestPracticeRouteId);

                    // if the BPR is not in the latest BPRs, then update the name of the route
                    if (!optimizedBPR) {
                        selectedRouteBPR.name = 'Old Star Route';
                    } else {
                        // if the BPR is in the latest BPRs, then update the route with the latest BPR
                        const selectedRouteIndex = routes.findIndex(route => route.routeId === currentSelectedRouteId);
                        selectedRouteBPR.name = optimizedBPR.name;
                        routes[selectedRouteIndex] = selectedRouteBPR;
                    }
                }

                // check if the top-ranked BPR is in the plan, if not add it to the plan
                if (!routes.some(route => route.bestPracticeRouteId === topRankedBPR.bestPracticeRouteId)) {
                    routes.push(topRankedBPR);
                }
            }

            // Banner when the plan's Star Routes were replaced, or when this recalculation was started because
            // a more fuel-efficient Star Route was precomputed ("Star Route Ranking Updated" banner showing).
            // The id is fixed per schedule, so it replaces that banner and repeated recalculations do not stack.
            const imo = starRouteAlertImo();
            const rankingAlertId = starRouteRankingAlertId(plan.scheduleId);
            if (bprExistsInLatestPlan || VesselAlertsModel!.getAlert(imo, rankingAlertId)) {
                VesselAlertsModel!.addAlert(imo, {
                    title: "Star Routes Updated",
                    message: "Star Route ranking have been updated based on the latest Weather forecast. ",
                    id: rankingAlertId,
                    type: AlertType.BestPracticeRouteUpdated,
                    scheduleId: plan.scheduleId,
                });
            }

            latestPlan!.routes = routes;

            // Update the plan in the model
            await VesselPlanningModel!.setPlan(plan.scheduleId, latestPlan!, false);
            this._setStarRoutesVisibility(routes, plan.scheduleId, true);

            this._rankedBPRsJobs.set(plan.scheduleId, { status: "complete" });
        } else {
            // Only reachable when triggerBprJob && !vesselIsOffline, i.e. after routes were hidden above.
            this._setStarRoutesVisibility(plan.routes, plan.scheduleId, true);
            VesselAlertsModel!.removeAlert(starRouteAlertImo(), starRouteRankingAlertId(plan.scheduleId));
            this._rankedBPRsJobs.set(plan.scheduleId, { status: "failed" });
            throw new Error("Failed to fetch ranked BPRs");
        }

        if (optimizedBPRs && optimizedBPRs.length > 0) {
            await IndexedDBService.bprs.add({
              scheduleId: plan.scheduleId,
              data: optimizedBPRs
            });
          } else {
              await IndexedDBService.bprs.delete(plan.scheduleId); //delete outdated bpr for that schedule
          }

        // Fetch and add default Open Ocean Route if the feature flag is enabled and voyage is long enough
        if (this.isOpenOceanRouteEnabled() && this._isLongVoyage(plan)) {
            await this._loadDefaultOOR(plan);
        }

        // Dispatch event to notify UI that ranked BPRs have been updated
        this.dispatchEvent(new CustomEvent(SuggestedRouteEvents.RankedBPRUpdated, {detail: {scheduleId: plan.scheduleId, bprOptimizedLength: optimizedBPRLength, ukcFailedRoutesLength: ukcFailedRoutesLength, errorDetails: result?.errorDetails}}));
    }

    async requestAnotherStarRoute(scheduleId: string, optimizedBPRs: BprAPIResponse[], noOfStarRoutesInPlan: number): Promise<void> {
        const latestPlan = await VesselPlanningModel!.getCurrentPlan(scheduleId);
        if (!latestPlan) {
            return;
        }
        
        const latestForecastVersionForPayload = WeatherModel!.latestForecastVersion;
        this._addBPRtoSchedule(optimizedBPRs,latestForecastVersionForPayload,latestPlan); // convert to desired format
        
        //iterate over the available BP routes and add the  one which is not existing in plan
        let nextBPR;
        let foundNextBPR = false;
        optimizedBPRs.map((val,index)=>{
            if (!foundNextBPR) {
                nextBPR = this._BPRsBySchedule.get(scheduleId)![index];
                if(this._checkBPRIdInPlan(latestPlan,nextBPR)){
                    foundNextBPR = true;
                }
            }
        })

        if(nextBPR) {
            latestPlan!.routes.push(nextBPR);
            await VesselPlanningModel!.setPlan(scheduleId, latestPlan!, false);           
        }
    }

    async requestAnotherOpenOceanRoute(scheduleId: string, optimizedOORs: BprAPIResponse[], noOfOORsInPlan: number): Promise<void> {
        const latestPlan = await VesselPlanningModel!.getCurrentPlan(scheduleId);
        if (!latestPlan) {
            return;
        }

        const latestForecastVersionForPayload = WeatherModel!.latestForecastVersion;
        this._addOORtoSchedule(optimizedOORs, latestForecastVersionForPayload, latestPlan);

        let nextOOR: VoyageRoute | undefined;
        let foundNextOOR = false;
        optimizedOORs.forEach((_, index) => {
            if (!foundNextOOR) {
                const candidate = this._OORsBySchedule.get(scheduleId)![index];
                if (this._checkOORIdInPlan(latestPlan, candidate)) {
                    nextOOR = candidate;
                    foundNextOOR = true;
                }
            }
        });

        if (nextOOR) {
            latestPlan.routes.push(nextOOR);
            await VesselPlanningModel!.setPlan(scheduleId, latestPlan, false);
        }
    }

    private async _loadDefaultOOR(plan: VoyagePlan): Promise<void> {
        try {
            const optimizedOORs = await VesselPlansService.getOptimizedOORsByPortPair(
                plan.departureTerminalCode,
                plan.arrivalTerminalCode,
                plan.scheduleId
            );

            if (!optimizedOORs || optimizedOORs.length === 0) {
                await IndexedDBService.oors.delete(plan.scheduleId);
                return;
            }

            await IndexedDBService.oors.add({ scheduleId: plan.scheduleId, data: optimizedOORs });

            const latestPlan = await VesselPlanningModel!.getCurrentPlan(plan.scheduleId);
            if (!latestPlan) return;

            const latestForecastVersionForPayload = WeatherModel!.latestForecastVersion;
            this._addOORtoSchedule(optimizedOORs, latestForecastVersionForPayload, latestPlan);

            const oorAlreadyInPlan = latestPlan.routes.find(r => r.uiType === 'OOR');
            if (!oorAlreadyInPlan) {
                const firstOOR = this._OORsBySchedule.get(plan.scheduleId)?.[0];
                if (firstOOR) {
                    latestPlan.routes.push(firstOOR);
                    await VesselPlanningModel!.setPlan(plan.scheduleId, latestPlan, false);
                }
            }
        } catch (error) {
            console.error('Error loading default OOR:', error);
        }
    }

    private _addOORtoSchedule(optimizedOORs: BprAPIResponse[], latestForecastVersionForPayload: string, latestPlan: VoyagePlan): void {
        this._OORsBySchedule.set(latestPlan.scheduleId, []);
        for (const hydraRoute of optimizedOORs) {
            const oorRoute = hydraRoute.routes;
            const routeGeometry = oorRoute.route_geometry;
            const rank = hydraRoute.fuel_rank || hydraRoute.rank;
            const route: VoyageRoute = this._getRoute(routeGeometry, oorRoute.best_practice_route_id, rank, 'OOR');
            route.uiType = 'OOR';

            const endpoint = oorRoute.route_geometry[oorRoute.route_geometry.length - 1];
            const resultDuration = new Date(endpoint.properties.timestamp!).getTime() - new Date(oorRoute.etd).getTime();
            route.route_info = {
                eta: new Date(oorRoute.route_info?.eta)?.toISOString(),
                etd: new Date(latestPlan.etd).toISOString(),
                speed_over_ground: oorRoute.route_info?.speed_over_ground,
                co2: -1,
                totals: {
                    eca: { time: 0, fuel_cost: 0, vessel_cost: 0, distance: 0, fuel: 0 },
                    non_eca: {
                        time: resultDuration / 60 * 60 * 1000,
                        fuel_cost: 0,
                        vessel_cost: 0,
                        distance: oorRoute.route_info?.totals?.non_eca?.distance,
                        fuel: oorRoute.route_info?.totals?.non_eca?.fuel
                    }
                },
                rpm: -1,
                weather: {
                    forecast_versions: [],
                    subscriptions: [],
                    summary: oorRoute.route_info?.weather?.summary
                },
                calculationInput: {
                    forecast_versions: [latestForecastVersionForPayload],
                    draftFore: latestPlan.foreDraft,
                    draftAft: latestPlan.aftDraft,
                    gmt: latestPlan.gmt || 0,
                    etd: new Date(latestPlan.etd).toISOString(),
                    eta: new Date(latestPlan.eta).toISOString(),
                    weatherLimits: VoyageRoutingModel!.getWeatherLimits()
                },
                hydraErrors: oorRoute.route_info?.hydraErrors,
            };
            addPrefillValuesToPassagePlan(route.passagePlan!);
            this._OORsBySchedule.get(latestPlan.scheduleId)!.push(route);
        }
    }

    private _checkOORIdInPlan(latestPlan: VoyagePlan, nextOOR: VoyageRoute): boolean {
        if (latestPlan.routes.find(route => route.bestPracticeRouteId === nextOOR.bestPracticeRouteId)) {
            return false;
        }
        const deleted = this._deletedOOR.get(latestPlan.scheduleId);
        if (deleted && deleted.includes(nextOOR.bestPracticeRouteId!)) {
            return false;
        }
        return true;
    }

        private _getRoute(routeGeometry:RouteGeometry, id: string, rank?:number, bprUiType?: string): VoyageRoute {
            return {
            routeId: UUID(),
            uiType: `BPR`,
            routeCreatedBy: 'system',
            name: bprUiType === 'OOR' ? `Open Ocean ${rank}` : `Star Route ${rank}`,
            route_geometry: routeGeometry,
            bestPracticeRouteId: id,
            creationTime: new Date().toISOString(),
            passagePlan: {
                description: "",
                reminders: "",
                notEcdisCompliant: false,
                encCellsUsed: "",
                booksAndTables: "",
                chartsAndLightsNotes: "",
                sailingDirections: "",
                listOfRadioSignals: "",
                listOfLights: "",
                gpsEcdisRadarMap: "",
                paperCharts: "",
                pubCorrectedToWeek: "",
                tideTables: "",
                publicationsNotes: "",
                mastersOrders: "",
                captain: "",
                navigationalOfficer: "",
                planDate: null,
                safetyMessages: {
                    navareaRegions: "",
                    navtexStations: ""
                },
                weatherInfo: {
                    navareaRegions: "",
                    navtexStations: "",
                    info: ""
                },
                equipmentStatus: {
                    operationalCondition: "",
                    failures: ""
                },
                weatherRouting: {
                    required: false,            
                    acknowledged: false,     
                    pilotPointAcknowledged: false 
                }
            }
        }
    }

    private _addBPRtoSchedule(optimizedBPRs: BprAPIResponse[],latestForecastVersionForPayload: string,latestPlan: VoyagePlan) {
        for (const hydraRoute of optimizedBPRs) {
            const bprRoute = hydraRoute.routes;
            const routeGeometry = bprRoute.route_geometry;
            const rank = hydraRoute.fuel_rank || hydraRoute.rank;
            const route: VoyageRoute = this._getRoute(routeGeometry, bprRoute!.best_practice_route_id, rank, bprRoute.uiType);

            // const startpoint = bprRoute.route_geometry[0];
            const endpoint = bprRoute.route_geometry[bprRoute.route_geometry.length - 1];
            const resultDuration = new Date(endpoint.properties.timestamp!).getTime() - new Date(bprRoute.etd).getTime();
            route.route_info = {
                eta: new Date(bprRoute.route_info?.eta)?.toISOString(),
                etd: new Date(latestPlan!.etd).toISOString(),
                speed_over_ground:  bprRoute.route_info?.speed_over_ground,
                co2: -1,
                totals: {
                    eca: { time: 0, fuel_cost: 0, vessel_cost: 0, distance: 0, fuel: 0 },
                    non_eca: {
                        time: resultDuration/60*60*1000, // convert to hours
                        fuel_cost: 0, vessel_cost: 0, // Not available
                        distance: bprRoute.route_info?.totals?.non_eca?.distance,
                        fuel:  bprRoute.route_info?.totals?.non_eca?.fuel
                    }
                },
                rpm: -1, // RPM for entire voyage? TODO - remove this, it is pretty useless
                weather: { // Weather will be added after this
                    forecast_versions: [ ],
                    subscriptions: [],
                    summary:  bprRoute.route_info?.weather?.summary
                },
                calculationInput: {
                    forecast_versions: [ latestForecastVersionForPayload ],
                    draftFore: latestPlan.foreDraft,
                    draftAft: latestPlan.aftDraft,
                    gmt: latestPlan.gmt || 0,
                    etd: new Date(latestPlan.etd).toISOString(),
                    eta: new Date(latestPlan.eta).toISOString(),
                    weatherLimits: VoyageRoutingModel!.getWeatherLimits()
                },
                hydraErrors: bprRoute.route_info?.hydraErrors,
            };
            addPrefillValuesToPassagePlan(route!.passagePlan!);
            
            if (this._BPRsBySchedule.has(latestPlan.scheduleId)) {
                this._BPRsBySchedule.get(latestPlan.scheduleId)!.push(route);
            } else {
                this._BPRsBySchedule.set(latestPlan.scheduleId, [route]);
            }
        }
    }

    private _checkBPRIdInPlan(latestPlan: VoyagePlan, nextBPR: VoyageRoute) {
        //check if nextBPR already exists in plan 
        if (!(latestPlan!.routes.find(route => route.bestPracticeRouteId === nextBPR.bestPracticeRouteId))){
            //check that is not in deletd items
            if(!this._deletedBPR.get(latestPlan.scheduleId) || !this._deletedBPR.get(latestPlan.scheduleId)?.length){
                return true;
            }
            else if(!this._deletedBPR.get(latestPlan.scheduleId)?.find(bprId => bprId === nextBPR.bestPracticeRouteId)) {
                return true;
            }
            return true;
        } 
        return false;
    }

     updateDeletedStarRoute(scheduleId: string, bprId: string, reset: boolean) {
        const latestPlan = VesselPlanningModel!.getCurrentPlan(scheduleId);
        let deltedItems = this._deletedBPR.get(scheduleId);
        if(reset) {
            this._deletedBPR.set(scheduleId, []);
        }
        else {
            deltedItems?.push(bprId);
            this._deletedBPR.set(scheduleId, deltedItems || [bprId]);
        }
        
    }

    private _convertRouteGeometry(routeGeometry: RouteGeometry): RouteGeometry {
       return routeGeometry.map((feature) => {
           if (feature.properties.type === 'Hard') {
                const newFeature = {
                    ...feature,
                    properties: {
                        ...feature.properties,
                        origin: FeatureOrigin.System,
                    },
                }
                return newFeature;
            } else {
                return feature
            }
       })
    }

}
